import React from 'react';
import {Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import ClassificationFieldsGroup from '../../../../../components/classification/ClassificationFieldsGroup';
import useClassificationFields from '../../../../../components/classification/useClassificationFields';
import {fetchSourceFile, updateSourceClassification} from './sourcesApi';
import useSourcesFileViewerStore from '../../../../../stores/useSourcesFileViewerStore';

/**
 * "Edit classification" dialog for an already-added source, opened from the document header.
 *
 * Loads the item's current Classification Level/Distribution/Compartment from the detail
 * endpoint (same data the header renders), lets the user change it via the shared
 * `ClassificationFieldsGroup`, and saves via `updateSourceClassification`. Only the specific
 * row/reference being edited is updated — matches the existing rename/move behavior of not
 * propagating to other rows of the same underlying file.
 */
const SourcesEditClassificationDialog = ({open, onClose, file, userId, onSaved}) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState(null);

    const {level, distribution, compartment, setLevel, setDistribution, setCompartment, fieldsProps} = useClassificationFields('U');

    React.useEffect(() => {
        if (!open || !file?.id || !userId) {
            setError(null);
            return undefined;
        }

        const controller = new AbortController();
        setIsLoading(true);
        setError(null);

        fetchSourceFile({fileId: file.id, userId, signal: controller.signal})
            .then((payload) => {
                const item = payload?.item || payload?.data || payload?.result || payload || {};
                const classification = item?.classification || {};
                setLevel(classification.level || 'U');
                setDistribution(Array.isArray(classification.distribution) ? classification.distribution : []);
                setCompartment(Array.isArray(classification.compartment) ? classification.compartment : []);
            })
            .catch((err) => {
                if (err?.name !== 'AbortError') {
                    setError(err?.message || 'Failed to load current classification.');
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, file?.id, userId]);

    const handleSave = React.useCallback(async () => {
        if (!file?.id || !userId) return;
        setIsSaving(true);
        setError(null);
        try {
            await updateSourceClassification({
                fileId: file.id,
                userId,
                classificationLevel: level,
                distribution,
                compartment,
            });
            // Patch any already-open viewer for this file (Sources file viewer modal) so its
            // header reflects the new classification immediately, without a close/reopen.
            useSourcesFileViewerStore.getState().notifyClassificationUpdated({
                fileId: file.id,
                classification: {level, distribution, compartment},
            });
            onSaved?.({level, distribution, compartment});
            onClose?.();
        } catch (err) {
            setError(err?.message || 'Failed to update classification.');
        } finally {
            setIsSaving(false);
        }
    }, [file?.id, userId, level, distribution, compartment, onSaved, onClose]);

    const fileName = file?.name || 'File';

    return (
        <Dialog
            open={open}
            onClose={isSaving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            aria-label="Edit classification"
            // Default `sm` caps at 600px; bump the paper's own max-width to 720px (+20%) so the
            // dialog has more breathing room for the classification fields.
            PaperProps={{sx: {maxWidth: 720}}}
        >
            <DialogTitle noWrap title={fileName}>
                Edit classification — {fileName}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {isLoading ? (
                        <Stack alignItems="center" justifyContent="center" py={3}>
                            <CircularProgress size={24}/>
                        </Stack>
                    ) : (
                        <ClassificationFieldsGroup idPrefix="edit-source-classification" {...fieldsProps}/>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={isLoading || isSaving}>
                    {isSaving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(SourcesEditClassificationDialog);
